/* eslint-disable react/require-default-props */
/* eslint-disable import/max-dependencies */
import type React from "react";
import {
  Table,
  SelectSearchComponent,
  ShowMoreItems,
} from "@fas/ui-core";
import type { Column } from "@fas/ui-core";
import { Link, useNavigate } from "react-router";
import { useTable } from "@fas/ui-framework";
import { useDispatch } from "react-redux";
import { PAGES_LIST } from "../../helpers/constants";
import PagesActions from "./PagesActions";
import {
  EditGeneralAction,
  PreviewAction,
  CloneAction,
  ExportAction,
  ActivateAction,
} from "./PagesActionsButton";
import { getPagesListSaga } from "../../actions/pages/actions";
import { getHashValue } from "../../services/jusCan";
import type { Page, Version, Tag } from "./types/PagesList.types";

type PageList = Page & {
  id: string;
  url: string;
  status: string;
  version: string;
  tags: string[];
  lastEdit: string;
  author: string;
  actions: React.ReactNode;
} & Omit<Page, "openId">;

const columns: Array<Column<PageList>> = [
  {
    field: "id",
    label: "ID",
    sortable: false,
    searchable: true,
    render: ({ id, documentId }) => (
      <Link to={`/pages/edit/${documentId}?hashId=${getHashValue()}`}>
        {id}
      </Link>
    ),
  },
  {
    field: "name",
    label: "Name",
    sortable: true,
    searchable: true,
  },
  {
    field: "url",
    label: "Url",
    sortable: false,
    searchable: false,
    render: (row) => {
      const last = row.aliasCollection[row.aliasCollection.length - 1];
      return last && last.alias;
    },
  },
  {
    field: "type",
    label: "Type",
    sortable: true,
    searchable: true,
    SearchComponent: (searchProps) => (
      <SelectSearchComponent
        {...searchProps}
        values={[
          { value: "", title: "Choose type" },
          { value: "template", title: "Template" },
          { value: "page", title: "Page" },
          { value: "pft", title: "PFT" },
          { value: "custom", title: "Custom" },
        ]}
      />
    ),
  },
  {
    field: "status",
    label: "Status",
    sortable: false,
    searchable: false,
    render: (row) => {
      const isNew = row.versionCollection.every(
        ({ version }) => version === "new"
      );
      return isNew ? "New" : "Active";
    },
  },
  {
    field: "version",
    label: "Version",
    sortable: false,
    searchable: false,
    render: (row) => {
      const versions = row.versionCollection.map(({ version }) => version);
      let version = "";
      if (
        (versions.includes("new") && versions.includes("current"))
        || versions.includes("new")
      ) {
        version = "Edit";
      }
      else if (versions.includes("current")) {
        version = "Published";
      }
      return version;
    },
  },
  {
    field: "tagCollection",
    label: "Tags",
    sortable: false,
    searchable: false,
    render: (row) => (<ShowMoreItems data={row.tagCollection.map((i) => i.tag)} />),
  },
  {
    field: "updatedAt",
    label: "Last edit",
    sortable: false,
    searchable: false,
  },
  {
    field: "author",
    label: "Author",
    sortable: false,
    searchable: false,
    render: (row) => {
      const item = row.versionCollection[row.versionCollection.length - 1];
      return (item && item.lastEditor) || "None";
    },
  },
  {
    field: "actions",
    label: "",
    sortable: false,
    searchable: false,
    render: (row) => (
      <>
        <EditGeneralAction item={row} />
        <PreviewAction item={row} />
        <CloneAction item={row} />
        <ExportAction item={row} />
        <ActivateAction item={row} />
      </>
    ),
  },
];

function PagesList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { onDeselectAll, ...tableProps } = useTable<PageList>(PAGES_LIST);
  const onLoad = () => dispatch(getPagesListSaga());
  const onCreate = () => navigate(`/pages/create?hashId=${getHashValue()}`);

  return (
    <Table
      {...tableProps}
      title="Pages"
      columns={columns}
      onLoad={onLoad}
      onCreate={onCreate}
      rowSelectAvailable={() => true}
      allSelectAvailable={() => true}
      Actions={<PagesActions />}
      onDeselectAll={() => onDeselectAll([])}
    />
  );
}

export default PagesList;
